import { pLinkExternalMapper } from 'data-source/mappers/pLinkMapper'
import { reviewProductScoreMapper } from 'data-source/mappers/reviewProductScoreMapper'
import type { ApiReviewProductPartProps, ApiReviewProductProps } from 'data-source/types.wp'
import type {
  DSFeatureIcon,
  DSFeatureProp,
  DSProductFeatureProps,
  DSReviewProductPart,
} from 'shared-definitions/types'

export function reviewProductPartMapper(
  input: ApiReviewProductPartProps
): Omit<DSReviewProductPart, 'image' | 'badges'> {
  return {
    resource: 'review-product-page',
    table: {
      positive: input.table.positives,
      negative: input.table.negatives,
    },
    storeButton: input.store_button
      ? {
          store: input.store_button.store,
          link: {
            label: input.store_button.label,
            pLink: pLinkExternalMapper(input.store_button.url),
          },
        }
      : null,
    device: {
      name: input.review.manufacturer
        ? `${input.review.manufacturer} ${input.review.device}`
        : input.review.device,
      ourScore: input.review.rating && input.review.rating > 0 ? input.review.rating : null,
      userScore:
        input.review.user_rating && input.review.user_rating > 0 ? input.review.user_rating : null,
    },
    features: reviewProductFeatures(input.review),
    scores: input.review.ratings.map(reviewProductScoreMapper),
    mdaqs: input.review.mdaqs?.length
      ? {
          title: 'MDAQS rating',
          link: {
            label: 'Learn more',
            pLink: {
              href: 'https://www.soundguys.com/what-is-mdaqs-the-multi-dimensional-audio-quality-score-explained-104949/',
              target: '_blank',
            },
          },
          maxValue: 5,
          scores: input.review.mdaqs.map(i => ({
            name: i.mdaqs_name,
            value: i.admin_rating,
          })),
        }
      : null,
    bottomLineContent: input.table.bottom_line,
  }
}

function reviewProductFeatures({
  details,
  features,
}: ApiReviewProductProps): DSProductFeatureProps[] {
  if (features && features.length > 0) {
    return features.map(i => ({
      prop: i.icon,
      val: [i.value],
      desc: i.name,
    }))
  }

  return details
    .map(i => ({
      prop: convertDetailsKeyToIcon(i.key),
      val: i.content,
      desc: i.name,
    }))
    .filter(
      i =>
        !(
          ['_weight', '_product_dimensions', '_item_model_number'].includes(i.prop) &&
          i.val.length === 0
        )
    )
}

function convertDetailsKeyToIcon(input: DSFeatureProp): DSFeatureIcon {
  const map: Record<DSFeatureProp, DSFeatureIcon> = {
    _3d: '3d',
    _network_bars: 'network_bars',
    _noisy: 'noisy',
    _music_note: 'music_note',
    _headphones: 'headphones',
    _charger: 'charger',
    _battery: 'battery',
    _microphone: 'microphone',
    _release_date: 'checkmark-screen',
    _price: 'coins',
    _weight: 'dumbbell',
    _product_dimensions: 'dimensions',
    _waterproof: 'drop',
    _item_model_number: 'barcode',
  }

  return map[input]
}
